import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Button, Card, Form } from 'react-bootstrap';
import './RichTextEditor.css';

const RichTextEditor = ({ 
  initialValue = '', 
  onSave,
  onChange,
  placeholder = 'Escribe tu contenido aquí...',
  toolbar = 'full',
  height = '300px',
  showHeader = true,
  headerTitle = 'Editor de Contenido'
}) => {
  const [editorContent, setEditorContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const quillRef = useRef(null);

  // Este useEffect es crucial para inicializar correctamente el contenido HTML
  useEffect(() => {
    console.log("initialValue cambiado:", initialValue);
    setEditorContent(initialValue || '');
  }, [initialValue]);

  // Configuración de la barra de herramientas
  const toolbarOptions = {
    full: [
      [{ 'font': [] }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean'],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'direction': 'rtl' }],
      ['blockquote', 'code-block'],
    ],
    basic: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
    email: [
      ['bold', 'italic', 'underline'],
      [{ 'color': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ]
  };

  // Esta función es clave para el problema: 
  // Propaga el HTML al componente padre además de actualizarlo localmente
  const handleChange = (content) => {
    console.log("Editor cambió:", content); // Depuración
    setEditorContent(content);
    
    // Propaga el cambio al componente padre si existe una función onChange
    if (typeof onChange === 'function') {
      onChange(content);
    }
  };

  const handleSave = async () => {
    if (typeof onSave === 'function') {
      setIsLoading(true);
      try {
        await onSave(editorContent);
      } catch (error) {
        console.error('Error al guardar contenido del editor:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Función para sobrescribir el contenido programáticamente (si es necesario)
  const setContent = (content) => {
    setEditorContent(content);
  };

  // Función para obtener el contenido actual
  const getContent = () => {
    return editorContent;
  };

  // Exponer las funciones para uso externo
  if (quillRef.current) {
    quillRef.current.setContent = setContent;
    quillRef.current.getContent = getContent;
  }

  // Módulos de Quill para personalización adicional
  const modules = {
    toolbar: toolbarOptions[toolbar] || toolbarOptions.basic,
    clipboard: {
      matchVisual: false // Mejora la conservación de formato al pegar
    }
  };

  // Verificar si el editor está renderizando correctamente (para depuración)
  console.log("Renderizando editor:", {
    editorContent,
    height,
    showHeader,
    toolbar
  });

  return (
    <Card className="rich-text-editor-container mb-4 shadow-sm">
      {showHeader && (
        <Card.Header className="bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">{headerTitle}</h5>
            {onSave && (
              <Button 
                variant="primary" 
                onClick={handleSave} 
                disabled={isLoading}
              >
                {isLoading ? 'Guardando...' : 'Guardar Contenido'}
              </Button>
            )}
          </div>
        </Card.Header>
      )}
      <Card.Body className={!showHeader ? 'pt-0 px-0 pb-0' : ''}>
        <div style={{ height }} className="quill-wrapper">
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={editorContent}
            onChange={handleChange}
            placeholder={placeholder}
            modules={modules}
            style={{ height: 'calc(100% - 5px)' }}
            preserveWhitespace={true}
          />
        </div>
      </Card.Body>
    </Card>
  );
};

export default RichTextEditor;